import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { CHAT_ENTITY_VIEWS, RenderEntityViewOutputSchema } from "@bliks-io/contracts";
import { CardContent, CardHeader } from "../../base/card/card";
import { ToolPartBlock } from "./tool-part-block";
import { ChatPartCard, ChatPartNotice } from "./chat-part-shell";
import { ChatEntityViewSkeleton, ChatEntityViewStateNotice } from "./chat-entity-view-shell";
import { ProcessStepView, StepDecisionRulesView } from "./chat-step-views";
import { ProcessFlowView } from "./chat-process-flow-view";
import { useEntityCard } from "./use-entity-card";
/** The contracted views, as a set — every one of them has a renderer. */ const DRAWABLE_VIEWS = new Set(CHAT_ENTITY_VIEWS);
/**
 * Whether the transcript can draw this entity-view part.
 *
 * One tool mints one part type for every view, so an older bundle has to read
 * the discriminator to skip a newer one. `true` while the call streams, and for
 * a payload with no readable `view` — MALFORMED output, which belongs in the
 * error card. `false` only once the payload names a view this bundle has no
 * renderer for: a forward-compat gap, which must render NOTHING.
 *
 * The RAW discriminator is read before any parse, because a failed parse is
 * what `ToolPartBlock` turns into the error card.
 */ export function rendersEntityView(part) {
    const view = part.output?.view;
    return typeof view !== "string" || DRAWABLE_VIEWS.has(view);
}
/**
 * One person, system, team or external contact, for the `entityCard` parts
 * older transcripts still hold — deleting this would blank those messages. A
 * mention's record is carried by the hover card on the chip itself
 * (`EntityMentionHoverCard`).
 *
 * The card resolves out of the shared assignee lookup, which is also where the
 * hover card gets its own — one decision, in `useEntityCard`, so the two
 * surfaces cannot disagree about whether a record is loading, gone or there.
 *
 * No link home, unlike the process-shaped views: these four have an `entity://`
 * type of their own, so the prose beside the card already carries the mention
 * that navigates (plan 016 §8). It IS labelled as the current view, because a
 * chip and this card can name the same entity in one message and disagree after
 * a rename. No `updatedAt`: the lookup shapes do not carry one.
 *
 * Memoised on its ids — the assistant keeps streaming text after the part lands.
 */ const EntityCardView = /*#__PURE__*/ memo(function EntityCardView({ data, entityType, entityId }) {
    const { t } = useTranslation();
    const state = useEntityCard(data, entityType, entityId);
    // The shared notice speaks about the VIEW, which is right for the
    // process-shaped views it also serves and wrong here: what is gone is the
    // record, and the hover card on a mention chip says exactly that for the
    // identical condition.
    if (state.status === "unavailable") {
        return /*#__PURE__*/ _jsx(ChatPartNotice, {
            message: t("chat.entityCard.unavailable")
        });
    }
    if (state.status !== "ready") return /*#__PURE__*/ _jsx(ChatEntityViewStateNotice, {
        state: state
    });
    return /*#__PURE__*/ _jsxs(ChatPartCard, {
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                className: "px-4 pb-0 pt-3",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-muted-foreground",
                    children: t("chat.entityView.current")
                })
            }),
            /*#__PURE__*/ _jsx(CardContent, {
                className: "px-2 pb-2",
                children: state.card
            })
        ]
    });
});
/** Draws whatever state a `renderEntityView` part is in. */ export function ChatEntityViewBlock({ part, slug, data }) {
    const { t } = useTranslation();
    // An unknown view is decided before any parse — see `rendersEntityView`.
    if (!rendersEntityView(part)) return null;
    return /*#__PURE__*/ _jsx(ToolPartBlock, {
        part: part,
        schema: RenderEntityViewOutputSchema,
        skeleton: /*#__PURE__*/ _jsx(ChatEntityViewSkeleton, {}),
        // No retry on this one: there is no fetch to repeat, the part itself is
        // what could not be read.
        error: /*#__PURE__*/ _jsx(ChatPartNotice, {
            message: t("chat.entityView.loadFailed")
        }),
        children: (output)=>{
            switch(output.view){
                case "processStep":
                    return /*#__PURE__*/ _jsx(ProcessStepView, {
                        data: data,
                        processId: output.processId,
                        stepRecordingId: output.stepRecordingId,
                        slug: slug
                    });
                case "stepDecisionRules":
                    return /*#__PURE__*/ _jsx(StepDecisionRulesView, {
                        data: data,
                        processId: output.processId,
                        stepRecordingId: output.stepRecordingId,
                        slug: slug
                    });
                case "processFlow":
                    return /*#__PURE__*/ _jsx(ProcessFlowView, {
                        data: data,
                        processId: output.processId,
                        slug: slug
                    });
                case "entityCard":
                    return /*#__PURE__*/ _jsx(EntityCardView, {
                        data: data,
                        entityType: output.entityType,
                        entityId: output.entityId
                    });
                default:
                    {
                        // The render prop returns `ReactNode`, which includes `undefined`,
                        // so a contracted view with no arm here would silently draw nothing
                        // — and, through `hasVisibleContent`, take its message row with it.
                        const unhandled = output;
                        throw new Error(`unhandled entity view: ${JSON.stringify(unhandled)}`);
                    }
            }
        }
    });
}

//# sourceMappingURL=chat-entity-view.js.map